import { useState, useRef, useEffect } from 'react'
import { ChevronsUpDown, Check } from 'lucide-react'
import { useBrand } from '../hooks/useBrand'
import { useAccount } from '@/hooks/useAccount';

/* ==================================================================
 * BrandSwitcher — global top-right brand / workspace switcher
 * ==================================================================
 * Rendered once (in AuthenticatedApp) so it shows on every page. It's
 * deliberately styled as an ACCOUNT control, not a content filter:
 * a ringed brand avatar, a two-line "Brand / name" label, and the
 * ChevronsUpDown switcher glyph (vs the ChevronDown filters use) — so
 * it never reads as one of a page's filter dropdowns. Only shown to
 * users in more than one brand. Switching persists + hard-reloads
 * (see useBrand.switchBrand). Lives here (not the sidebar) so it won't
 * collide with the upcoming sidebar-brands feature.
 * ================================================================== */

function Avatar({ name, logo, size, ring }: { name: string; logo: string | null; size: number; ring?: boolean }) {
  return (
    <span
      style={{
        position: 'relative', width: size, height: size, flexShrink: 0, borderRadius: '50%',
        boxShadow: ring ? '0 0 0 1.5px rgba(251,58,174,0.55)' : undefined,
      }}
    >
      <span style={{
        position: 'absolute', inset: 0, borderRadius: '50%',
        background: 'linear-gradient(135deg, #FB3AAE 0%, #4A90E2 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', fontSize: Math.round(size * 0.44), fontWeight: 700,
      }}>{(name || '?').charAt(0).toUpperCase()}</span>
      {logo && (
        <img
          src={logo}
          alt=""
          style={{ position: 'absolute', inset: 0, width: size, height: size, borderRadius: '50%', objectFit: 'cover', background: 'var(--bg-tertiary)' }}
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
        />
      )}
    </span>
  )
}

export default function BrandSwitcher() {
  const { brand, brands, switchBrand } = useBrand()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  const { isAgency, inBrand } = useAccount()

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  // Nothing to switch between — hide entirely for single-brand users.
  if (!brand || brands.length <= 1) return null
  const active = brands.find(b => b.id === brand.id)
  const activeName = active?.brand_name ?? brand.brand_name

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ ...trigger, ...(open ? triggerActive : {}) }}
        onMouseEnter={(e) => { if (!open) e.currentTarget.style.borderColor = 'rgba(255,255,255,0.22)' }}
        onMouseLeave={(e) => { if (!open) e.currentTarget.style.borderColor = 'var(--border-primary)' }}
        title="Switch brand"
        aria-label={`Current brand: ${activeName}. Switch brand.`}
      >
        <Avatar name={activeName} logo={active?.logo_url ?? null} size={28} ring />
        <span style={triggerText}>
          <span style={eyebrow}>Brand</span>
          <span style={triggerName}>{activeName}</span>
        </span>
        <ChevronsUpDown size={15} color="var(--text-tertiary)" style={{ flexShrink: 0 }} />
      </button>

      {open && (
        <div className="brand-menu-scroll" style={menu}>
          <div style={menuLabel}>Your brands</div>
          {brands.map(b => {
            const isActive = b.id === brand.id
            return (
              <button
                key={b.id}
                style={{ ...item, ...(isActive ? itemActive : {}) }}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = 'var(--bg-secondary)' }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent' }}
                onClick={() => { if (!isActive) switchBrand(b.id); else setOpen(false) }}
                title={b.brand_name}
              >
                <Avatar name={b.brand_name} logo={b.logo_url} size={26} ring={isActive} />
                <span style={itemText}>
                  <span style={itemName}>{b.brand_name}</span>
                  <span style={itemRole}>{b.role}{b.is_owner ? ' · Owner' : ''}</span>
                </span>
                {isActive && <Check size={15} strokeWidth={2.25} color="#FB3AAE" style={{ flexShrink: 0 }} />}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

const trigger: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 10,
  maxWidth: 260, padding: '6px 12px 6px 8px',
  background: 'var(--bg-primary)',
  border: '1px solid var(--border-primary)',
  borderRadius: 12, cursor: 'pointer', fontFamily: 'inherit',
  boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
  transition: 'border-color .15s ease, background .15s ease',
}
const triggerActive: React.CSSProperties = {
  background: 'var(--bg-secondary)',
  borderColor: 'rgba(251,58,174,0.45)',
}
const triggerText: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0, lineHeight: 1.15,
}
const eyebrow: React.CSSProperties = {
  fontSize: 9, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'var(--text-tertiary)',
}
const triggerName: React.CSSProperties = {
  fontSize: 13, fontWeight: 650, color: 'var(--text-primary)',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180,
}
const menu: React.CSSProperties = {
  // Left-aligned: the trigger sits at the top-LEFT, so open into the content
  // area (rightward) — never leftward over the sidebar. Matches the trigger's
  // width so it reads as one control.
  position: 'absolute', top: 'calc(100% + 8px)', left: 0, minWidth: 280, maxWidth: 340,
  background: 'var(--bg-primary)', border: '1px solid var(--border-primary)',
  borderRadius: 14, padding: 6, zIndex: 200,
  boxShadow: '0 2px 4px rgba(0,0,0,0.4), 0 18px 44px rgba(0,0,0,0.55)',
  display: 'flex', flexDirection: 'column', gap: 2,
  // Opens DOWNWARD from a trigger near the top of the viewport, so a long list
  // (an agency with a dozen brands, or someone on several teams) ran off the
  // bottom and clipped whatever sat at the end. Cap to the space that exists and
  // scroll. ~120px covers the top bar the trigger lives in plus the 8px gap and a
  // bottom margin.
  maxHeight: 'calc(100dvh - 120px)', overflowY: 'auto', overscrollBehavior: 'contain',
}
const menuLabel: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)',
  textTransform: 'uppercase', letterSpacing: '0.07em', padding: '6px 10px 6px',
}
const item: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 11, width: '100%',
  padding: '9px 10px', background: 'transparent', border: 'none', borderRadius: 10,
  color: 'var(--text-primary)', fontFamily: 'inherit', textAlign: 'left', cursor: 'pointer',
  transition: 'background .12s ease',
}
const itemActive: React.CSSProperties = {
  background: 'rgba(251,58,174,0.08)',
}
const itemText: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1, lineHeight: 1.2,
}
const itemName: React.CSSProperties = {
  fontSize: 13, fontWeight: 600, color: 'var(--text-primary)',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
}
const itemRole: React.CSSProperties = {
  fontSize: 11, color: 'var(--text-tertiary)', textTransform: 'capitalize',
}
